import { addMonths, monthsBetween } from './date';

/** What is being dragged in the year plan. */
export type CalendarDrag =
  /** An investment without a month (from „Noch ohne Termin“). */
  | { kind: 'place'; investmentId: string }
  /** A scheduled investment, grabbed on `grabbedMonth` – moves the whole window. */
  | { kind: 'move'; investmentId: string; grabbedMonth: string }
  /** The first or last month of a window – changes just that edge. */
  | { kind: 'start' | 'end'; investmentId: string };

export interface Range {
  plannedFrom: string | null;
  plannedTo: string | null;
}

/** Drop target ids. */
export const UNSCHEDULE_TARGET = 'unschedule';
export const monthTarget = (month: string) => `month:${month}`;

/** A window of a single month is stored as `plannedTo: null` – one shape per state. */
const normalise = (from: string, to: string | null): Range => ({
  plannedFrom: from,
  plannedTo: to === null || to === from ? null : to,
});

/**
 * New planned window after dropping `drag` on `targetId`, or null when nothing changes
 * (or the drop makes no sense, e.g. an end before the start).
 */
export function computeDrop(drag: CalendarDrag, targetId: string, current: Range): Range | null {
  const from = current.plannedFrom;
  const to = current.plannedTo; // may be null: the window is then the single month `from`
  const lastMonth = to ?? from;

  if (targetId === UNSCHEDULE_TARGET) {
    if (drag.kind === 'start' || drag.kind === 'end' || from === null) return null;
    return { plannedFrom: null, plannedTo: null };
  }
  if (!targetId.startsWith('month:')) return null;
  const month = targetId.slice(6);

  let next: Range;
  switch (drag.kind) {
    case 'place':
      next = normalise(month, null);
      break;
    case 'move': {
      if (from === null) {
        next = normalise(month, null);
        break;
      }
      const offset = monthsBetween(drag.grabbedMonth, month);
      next = normalise(addMonths(from, offset), to === null ? null : addMonths(to, offset));
      break;
    }
    case 'start':
      if (from === null || (lastMonth !== null && month > lastMonth)) return null;
      next = normalise(month, lastMonth);
      break;
    case 'end':
      if (from === null || month < from) return null;
      next = normalise(from, month);
      break;
  }
  return next.plannedFrom === from && next.plannedTo === to ? null : next;
}
